/**
 * Riga wall clock for the planner (S6): the server stores instants, the workshop plans in Riga time whatever the
 * browser's zone. Days are `YYYY-MM-DD` strings; times are minutes since midnight.
 */

const TZ = 'Europe/Riga';
const PARTS = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

/** An instant's Riga day and minute of the day. */
export function riga(iso: string | Date): { day: string; minutes: number } {
  const parts = Object.fromEntries(PARTS.formatToParts(typeof iso === 'string' ? new Date(iso) : iso).map((p) => [p.type, p.value]));
  return { day: `${parts.year}-${parts.month}-${parts.day}`, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}

/** Riga's offset from UTC at an instant, in minutes (120 in winter, 180 in summer). */
function offsetAt(ms: number): number {
  const { day, minutes } = riga(new Date(ms));
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(y, m - 1, d) + minutes * 60_000 - Math.floor(ms / 60_000) * 60_000) / 60_000);
}

/** The instant of a Riga day and minute (minutes may reach 1440 — the next midnight). */
export function instant(day: string, minutes: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const naive = Date.UTC(y, m - 1, d) + minutes * 60_000;
  let ms = naive - offsetAt(naive) * 60_000;
  ms = naive - offsetAt(ms) * 60_000;
  return new Date(ms).toISOString();
}

/**
 * The instant of a Riga day and minute — or null when that wall-clock time does not exist (the spring gap, 03:00–03:59
 * on the last Sunday of March). An ambiguous time (the autumn fold, 03:00–03:59 on the last Sunday of October) is its
 * LATER occurrence (winter time); an existing booking keeps its own instant when its start is not changed.
 */
export function exactInstant(day: string, minutes: number): string | null {
  const at = instant(day, minutes);
  const back = riga(at);
  const target = minutes >= 1440 ? { day: addDays(day, Math.floor(minutes / 1440)), minutes: minutes % 1440 } : { day, minutes };
  return back.day === target.day && back.minutes === target.minutes ? at : null;
}

/**
 * An instant moved by `minutes` on the Riga wall clock (the planner grid): 02:30 dragged two hours on the spring night is
 * 04:30, not 05:30. Null when the wall-clock target does not exist.
 */
export function shiftWall(iso: string, minutes: number): string | null {
  const from = riga(iso);
  const total = from.minutes + minutes;
  const days = Math.floor(total / 1440);
  return exactInstant(addDays(from.day, days), total - days * 1440);
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** ISO weekday, 1 = Monday. */
export function weekday(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return ((new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7) + 1;
}

export function mondayOf(day: string): string {
  return addDays(day, 1 - weekday(day));
}

export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function monthEnd(day: string): string {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function hhmm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** "8–17", "8–12:30" — the design's short spans. */
export function shortSpan(start: number, end: number): string {
  const one = (v: number): string => (v % 60 === 0 ? String(Math.floor(v / 60)) : `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`);
  return `${one(start)}–${one(end)}`;
}

/** The part of a booking on a Riga day, in minutes of that day; null when it does not touch the day. */
export function onDay(b: { startsAt: string; endsAt: string }, day: string): { start: number; end: number } | null {
  const s = riga(b.startsAt);
  const e = riga(b.endsAt);
  const endDay = e.minutes === 0 ? addDays(e.day, -1) : e.day;
  if (s.day > day || endDay < day) return null;
  return { start: s.day === day ? s.minutes : 0, end: endDay === day ? (e.minutes === 0 ? 1440 : e.minutes) : 1440 };
}
